import type { ColumnDef } from '@tanstack/react-table';

import { api } from '@connected/api-client';
import { me } from '@connected/api-client/data/auth/me';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Button } from '@connected/ui/components/button';
import { Skeleton } from '@connected/ui/components/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Edit, PlusIcon, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { DataTable } from '@/components/data-table';
import { ExpandedRowsMobile } from '@/components/expanded-row';
import { LoadingPage } from '@/components/loading-page';
import { Observation } from '@/components/observation';
import SearchInput from '@/components/search-input';
import { type UserData, UserSettingsModal } from '@/features/user-settings/components/user-settings-modal';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

export const Route = createFileRoute('/_auth/dashboard/organizations/$organizationId/users/')({
    component: RouteComponent,
    beforeLoad: ({ context, params }) => {
        const hasCorrectRole = context.currentUser?.Roles?.some(
            role => role?.Name === 'Applicatie beheerder' || role?.Name === 'Elektra manager' || role?.Name === 'Account eigenaar',
        );
        if (!hasCorrectRole && !context.currentUser?.IsAdmin) {
            throw redirect({
                to: '/dashboard/organizations/$organizationId/requests',
                params: { organizationId: params.organizationId },
            });
        }
    },
});

function RouteComponent() {
    const { t } = useTranslation('dashboard');
    const { organizationId } = Route.useParams();
    const search = Route.useSearch() as { search?: string };
    const [isUserModalOpen, setUserModalOpen] = useState(false);
    const [actionPopout, setActionPopout] = useState<'create' | 'edit' | 'delete' | null>(null);

    const [selectedUser, setSelectedUser] = useState<UserData | null>(null);

    const [expandedId, setExpandedId] = useState<string | null>(null);

    const { data, isLoading, isError, error } = useQuery(elektraAppQueryOptions.organisation.management.usersListOptions({
        organisationId: organizationId,
    }));

    const roles = useQuery(elektraAppQueryOptions.organisation.management.organisationRolesGetOptions({
        organisationId: organizationId,
    }));

    const removeUser = api.useMutation(
        'delete',
        '/api/v2/users/delete',
        {
            onSuccess: () => {
                toast.success(t('dashboard.users.message.deleteUserSuccess'));
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'createNewUser',
                        error,
                    },
                    { apiHttpMethod: 'Delete', apiUrl: '/api/v2/users/delete' },
                );
                toast.error(
                    t(error?.Message ?? t('dashboard.users.message.deleteRoleFail')),
                );
            },
            onSettled: () => {
                queryClient.invalidateQueries(
                    {
                        queryKey: elektraAppQueryOptions.organisation.management.usersListOptions({ organisationId: organizationId }).queryKey,
                    },
                );
            },
        },
    );

    const columns = useMemo<ColumnDef<UserData>[]>(() => ([
        {
            accessorKey: 'Username',
            meta: {
                sticky: true,
                width: 10,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.users.table.username')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: UserData } }) => (
                <DataTable.Cell variant="bold">
                    <Button
                        variant="ghost"
                        className="sm:hidden mr-2"
                        onClick={() => setExpandedId(prev => prev === row?.original?.UserId ? null : row?.original?.UserId ?? null)}
                    >
                        {expandedId === row?.original?.UserId ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </Button>
                    <div>
                        {(!row?.original?.Settings?.FirstName && !row?.original?.Settings?.LastName)
                            ? (
                                    <span>{row?.original?.DisplayName}</span>
                                )
                            : (
                                    <>
                                        {row?.original?.Settings?.FirstName}
                                        {' '}
                                        {row?.original?.Settings?.MiddleName ? `${row?.original?.Settings?.MiddleName} ` : ''}
                                        {row?.original?.Settings?.LastName}
                                    </>
                                )}
                        <br />
                        <span className="text-text-primary/40">
                            {row?.original?.Email}
                        </span>
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Role',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.users.table.role')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="bold">
                    {row?.original?.Roles?.map(role => role.Name).join(', ')}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'ColorCode',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.users.table.colorCode')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell>
                    <div className="flex items-center justify-center ml-2">
                        <Observation color={row.original.Settings?.ColorCode ?? ''} children label="" small={true} />
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Department',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.users.table.department')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="bold">
                    {row?.original?.Settings?.Department}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Function',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.users.table.function')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="bold">
                    {row?.original?.Settings?.JobTitleDescription}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'PhoneNumber',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.users.table.phoneNumber')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="bold">
                    {row?.original?.Settings?.PhoneNumber}
                </DataTable.Cell>
            ),
        },
        {
            id: 'actionModal',
            meta: {
                sticky: true,
                width: 10,
            },
            header: '',
            cell: ({ row }: { row: { original: UserData } }) => {
                const currentUserId = me.useQuery().data?.UserId;
                if (row?.original?.UserId === currentUserId) {
                    return null;
                }
                return (
                    <div className="flex gap-1 items-center justify-end ml-auto">
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={async () => {
                                if (await showConfirm(t('dashboard.users.message.deleteUserConfirm'))) {
                                    removeUser.mutateAsync({
                                        params: {
                                            query: {
                                                UserId: row?.original?.UserId ?? '',
                                            },
                                        },
                                    });
                                }
                            }}
                        >
                            <Trash2 className="text-text-primary/40 size-5" />
                        </Button>
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={() => {
                                setUserModalOpen(true);
                                setActionPopout('edit');
                                setSelectedUser(row?.original || null);
                            }}
                        >
                            <Edit className="text-text-primary/40 size-5" />
                        </Button>
                    </div>
                );
            },
        },
    ]), [expandedId, removeUser, t]);

    const tableColumns = useMemo(
        () => isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [columns, isLoading],
    );

    const tableData = useMemo<UserData[]>(
        () => {
            if (isError) {
                toast.error(error?.Message);
                return [];
            }

            const searchTerm = search.search?.toLowerCase() || '';
            return data?.Users?.filter((user) => {
                if (!searchTerm)
                    return true;

                return [
                    user.Settings?.FirstName || '',
                    user.Settings?.MiddleName || '',
                    user.Settings?.LastName || '',
                    user.Settings?.ColorCode || '',
                    [
                        user.Settings?.FirstName,
                        user.Settings?.MiddleName,
                        user.Settings?.LastName,
                    ].filter(Boolean).join(' ') || user.DisplayName || '',
                    user.Email || '',
                    user.Settings?.Department || '',
                    user.Settings?.JobTitleDescription || '',
                    user.Settings?.PhoneNumber || '',
                    user.Roles?.map(role => role.Name).join('') || '',
                ].some(field => field?.toString().toLowerCase().includes(searchTerm));
            }) || [];
        },
        [isError, search.search, data?.Users, error?.Message],
    );

    if (isLoading || roles.isLoading) {
        return <LoadingPage />;
    }

    return (
        <div className="flex flex-col xs: lg:flex-wrap gap-8 justify-start">
            <div className="flex flex-col sm:flex-row justify-between">
                <div className="flex flex-col gap-1">
                    <h3 className="text-2xl font-semibold">
                        {t('dashboard.users.title')}
                    </h3>
                    <p className="text-md text-text-primary/70">
                        {t('dashboard.users.subtitle')}
                    </p>
                </div>
                <div className="flex gap-5 mt-5 sm:mt-0 sm:gap-2 flex-col sm:flex-row ml-3 sm:ml-0">
                    <Button
                        variant="link"
                        className="flex items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background"
                        onClick={() => {
                            setUserModalOpen(true);
                            setActionPopout('create');
                        }}
                    >
                        <PlusIcon className="w-5 h-5" />
                        {t('dashboard.users.title')}
                    </Button>
                    <SearchInput route={Route} />
                </div>
            </div>
            <DataTable
                columns={tableColumns}
                data={tableData}
                pageSize={10}
                expandedRow={row =>
                    expandedId && expandedId === row?.original?.UserId
                        ? (
                                <ExpandedRowsMobile columns={columns} data={row.original} />
                            )
                        : null}
            />
            <UserSettingsModal
                isOpen={isUserModalOpen}
                setIsOpen={setUserModalOpen}
                roles={roles.data}
                user={actionPopout === 'edit' ? selectedUser ?? undefined : undefined}
                organizationId={organizationId}
            />
        </div>
    );
}
